import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import EnergySavingsLeafOutlinedIcon from '@mui/icons-material/EnergySavingsLeafOutlined';
import ElectricCarOutlinedIcon from '@mui/icons-material/ElectricCarOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import heroFleet from './Asset/contact-desktop (1).jpg';
import atto1Image from './Asset/seagull.webp';
import dolphinImage from './Asset/byd-dolphin.webp';
import atto3Image from './Asset/byd-atto3.webp';
import m6Image from './Asset/byd-m6.webp';
import m6DmImage from './images/bydm6dm/menu-1.webp';
import sealImage from './Asset/byd-seal.webp';
import sealion7Image from './Asset/byd-sealion-7.webp';
import PageSeo, {
  SITE,
  absoluteUrl,
  breadcrumbJsonLd,
  localBusinessJsonLd,
} from './PageSeo';
import { createWhatsAppUrl } from './siteConfig';
import { trackEvent } from './analytics';

const fleetModels = [
  {
    name: 'BYD Atto 1',
    category: 'City Car EV',
    image: atto1Image,
    path: '/jelajahi-atto1',
    use: 'Mobilitas perkotaan, kunjungan klien, dan kendaraan operasional ringkas.',
  },
  {
    name: 'BYD Dolphin',
    category: 'Hatchback EV',
    image: dolphinImage,
    path: '/jelajahi-dolphin',
    use: 'Operasional harian dan mobilitas tim dengan format hatchback praktis.',
  },
  {
    name: 'BYD Atto 3',
    category: 'SUV EV',
    image: atto3Image,
    path: '/jelajahi-atto',
    use: 'Company car atau kendaraan manajemen dengan format SUV listrik.',
  },
  {
    name: 'BYD M6',
    category: 'MPV EV',
    image: m6Image,
    path: '/jelajahi-m6',
    use: 'Kebutuhan perusahaan yang memerlukan kabin MPV dan beberapa baris kursi.',
  },
  {
    name: 'BYD M6 DM',
    category: 'MPV Plug-in Hybrid',
    image: m6DmImage,
    path: '/jelajahi-m6-dm',
    use: 'Armada keluarga atau bisnis yang membutuhkan fleksibilitas listrik dan bensin.',
  },
  {
    name: 'BYD Seal',
    category: 'Sedan EV',
    image: sealImage,
    path: '/jelajahi-seal',
    use: 'Kendaraan eksekutif dan representasi perusahaan dalam format sedan listrik.',
  },
  {
    name: 'BYD Sealion 7',
    category: 'SUV EV',
    image: sealion7Image,
    path: '/jelajahi-sealion-7',
    use: 'SUV listrik untuk kebutuhan manajemen dan perjalanan operasional.',
  },
];

const useCases = [
  { icon: BusinessCenterOutlinedIcon, title: 'Kendaraan operasional', text: 'Untuk aktivitas tim lapangan, kunjungan klien, sales, dan operasional harian.' },
  { icon: GroupsOutlinedIcon, title: 'Company car', text: 'Kendaraan manajemen, direksi, atau program mobilitas karyawan perusahaan.' },
  { icon: LocalShippingOutlinedIcon, title: 'Rental & travel fleet', text: 'Kebutuhan perusahaan rental, travel, dan layanan transportasi bisnis.' },
  { icon: HotelOutlinedIcon, title: 'Hotel & hospitality', text: 'Armada antar-jemput tamu, kendaraan manajemen, dan operasional hospitality.' },
  { icon: ElectricCarOutlinedIcon, title: 'Transisi armada EV', text: 'Konsultasi awal bagi perusahaan yang mempertimbangkan kendaraan listrik.' },
  { icon: EnergySavingsLeafOutlinedIcon, title: 'Program sustainability', text: 'Mendukung inisiatif mobilitas perusahaan dan arah ESG yang relevan.' },
];

const evBenefits = [
  {
    icon: SavingsOutlinedIcon,
    title: 'Energi operasional',
    text: 'Pengisian listrik memberi perusahaan alternatif sumber energi untuk mobilitas armada sehari-hari.',
  },
  {
    icon: BuildOutlinedIcon,
    title: 'Perawatan rutin lebih sederhana',
    text: 'EV tidak memakai sejumlah komponen rutin mesin pembakaran seperti oli mesin dan busi.',
  },
  {
    icon: EnergySavingsLeafOutlinedIcon,
    title: 'Arah ESG perusahaan',
    text: 'Armada listrik dapat menjadi bagian dari strategi sustainability dan transformasi mobilitas.',
  },
  {
    icon: ElectricCarOutlinedIcon,
    title: 'Teknologi kendaraan modern',
    text: 'BYD menghadirkan pilihan kendaraan elektrifikasi untuk beragam profil kebutuhan perusahaan.',
  },
];

const processSteps = [
  ['01', 'Konsultasi', 'Diskusikan jenis perusahaan, penggunaan kendaraan, kota operasional, dan target pengadaan.'],
  ['02', 'Tentukan kebutuhan', 'Pilih model, estimasi jumlah unit, registrasi, pembiayaan, dan lokasi pengiriman.'],
  ['03', 'Terima quotation', 'Tim sales menyiapkan informasi atau quotation berdasarkan kebutuhan yang disampaikan.'],
  ['04', 'Proses perusahaan', 'Lanjutkan evaluasi, dokumen, dan prosedur pembelian sesuai kebijakan perusahaan.'],
  ['05', 'Pengiriman kendaraan', 'Koordinasikan serah terima sesuai ketersediaan unit dan kesepakatan pembelian.'],
];

const faqItems = [
  {
    question: 'Apa itu pembelian BYD fleet?',
    answer: 'Pembelian BYD fleet adalah konsultasi dan pengadaan beberapa kendaraan BYD untuk kebutuhan perusahaan, organisasi, institusi, rental, hospitality, atau kegiatan operasional bisnis.',
  },
  {
    question: 'Apakah perusahaan bisa membeli BYD dalam jumlah banyak?',
    answer: 'Perusahaan dapat mengajukan kebutuhan beberapa unit. Ketersediaan model, jumlah unit, jadwal, dokumen, dan proses pembelian perlu dikonsultasikan berdasarkan kebutuhan perusahaan.',
  },
  {
    question: 'Apakah tersedia penawaran khusus untuk corporate fleet?',
    answer: 'Penawaran fleet dapat bergantung pada jumlah unit, model, registrasi, metode pembiayaan, lokasi pengiriman, dan program yang tersedia. Hubungi Junius untuk meminta informasi atau quotation berdasarkan kebutuhan aktual.',
  },
  {
    question: 'Model BYD apa yang cocok untuk kendaraan operasional perusahaan?',
    answer: 'Pemilihan model bergantung pada rute, jumlah penumpang, kebutuhan ruang, jabatan pengguna, dan pola operasional. Atto 1, Dolphin, Atto 3, M6, M6 DM, Seal, dan Sealion 7 dapat dibandingkan saat konsultasi.',
  },
  {
    question: 'Bagaimana cara meminta quotation BYD untuk perusahaan?',
    answer: 'Isi formulir fleet di halaman ini atau hubungi WhatsApp. Sertakan nama perusahaan, kota, estimasi jumlah unit, model yang diminati, serta kebutuhan registrasi atau pembiayaan.',
  },
  {
    question: 'Apakah pembelian fleet bisa untuk perusahaan rental?',
    answer: 'Perusahaan rental dapat berkonsultasi mengenai model dan jumlah unit yang dibutuhkan. Persyaratan dan penawaran akan mengikuti kondisi pengadaan yang berlaku.',
  },
  {
    question: 'Apakah BYD bisa digunakan sebagai kendaraan operasional perusahaan?',
    answer: 'Model BYD dapat dipertimbangkan untuk kendaraan operasional, company car, kendaraan manajemen, rental, hotel, travel, dan kebutuhan bisnis lain sesuai profil penggunaannya.',
  },
];

const initialForm = {
  name: '',
  company: '',
  position: '',
  whatsapp: '',
  email: '',
  city: '',
  units: '',
  model: '',
  notes: '',
};

function validateForm(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = 'Nama wajib diisi.';
  if (!values.company.trim()) errors.company = 'Nama perusahaan wajib diisi.';
  if (!values.position.trim()) errors.position = 'Jabatan wajib diisi.';
  if (!/^\+?[0-9\s-]{8,18}$/.test(values.whatsapp.trim())) {
    errors.whatsapp = 'Masukkan nomor WhatsApp yang valid.';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Masukkan email perusahaan yang valid.';
  }
  if (!values.city.trim()) errors.city = 'Kota wajib diisi.';
  if (!Number.isInteger(Number(values.units)) || Number(values.units) < 1) {
    errors.units = 'Masukkan estimasi minimal 1 unit.';
  }
  if (!values.model) errors.model = 'Pilih model yang diminati.';
  return errors;
}

function Fleet() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const formStarted = useRef(false);

  useEffect(() => {
    trackEvent('fleet_page_view', { page_path: '/fleet' });
  }, []);

  const handleWhatsAppClick = (source) => {
    trackEvent('fleet_whatsapp_click', { source });
  };

  const handleFormStart = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    trackEvent('fleet_form_start', { form_name: 'fleet_inquiry' });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: undefined }));
    }
  };

  const selectModel = (model) => {
    setForm((current) => ({ ...current, model }));
    setErrors((current) => ({ ...current, model: undefined }));
    trackEvent('fleet_model_interest', { model_name: model });
    document.getElementById('fleet-inquiry')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const message = [
      'Halo Junius, saya ingin meminta penawaran BYD Fleet untuk perusahaan.',
      '',
      `Nama: ${form.name}`,
      `Perusahaan: ${form.company}`,
      `Jabatan: ${form.position}`,
      `WhatsApp: ${form.whatsapp}`,
      `Email: ${form.email}`,
      `Kota: ${form.city}`,
      `Estimasi unit: ${form.units}`,
      `Model diminati: ${form.model}`,
      `Catatan: ${form.notes || '-'}`,
    ].join('\n');

    trackEvent('fleet_form_submit', {
      form_name: 'fleet_inquiry',
      model_name: form.model,
      estimated_units: Number(form.units),
    });
    trackEvent('fleet_whatsapp_click', { source: 'form_submit' });
    window.open(createWhatsAppUrl(message), '_blank', 'noopener,noreferrer');
  };

  const whatsappUrl = createWhatsAppUrl();

  return (
    <>
      <PageSeo
        title='BYD Fleet Indonesia | Mobil Listrik untuk Perusahaan'
        description='Konsultasi BYD fleet untuk perusahaan di Jakarta, Tangerang, BSD, dan Jabodetabek. Minta quotation kendaraan operasional dan corporate fleet.'
        keywords={[
          'BYD fleet',
          'BYD fleet Indonesia',
          'BYD corporate Indonesia',
          'harga fleet BYD',
          'program fleet BYD',
          'pembelian BYD untuk perusahaan',
          'mobil listrik untuk perusahaan',
          'kendaraan operasional perusahaan',
          'pengadaan mobil listrik perusahaan',
          'quotation BYD fleet',
          'sales BYD fleet Jakarta',
          'BYD fleet Tangerang',
        ]}
        path='/fleet'
        image={heroFleet}
        jsonLd={[
          localBusinessJsonLd(),
          fleetWebPageJsonLd,
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'BYD Fleet & Corporate', path: '/fleet' },
          ]),
          faqJsonLd,
        ]}
      />

      <Box component='main' sx={{ overflow: 'hidden', backgroundColor: '#f4f7f6', pb: { xs: 9, md: 0 } }}>
        <HeroSection whatsappUrl={whatsappUrl} onWhatsAppClick={handleWhatsAppClick} />
        <UseCaseSection />
        <BenefitSection />
        <ModelSection onSelectModel={selectModel} />
        <ConsultationSection whatsappUrl={whatsappUrl} onWhatsAppClick={handleWhatsAppClick} />
        <ProcessSection />
        <InquirySection
          form={form}
          errors={errors}
          onChange={handleChange}
          onFocus={handleFormStart}
          onSubmit={handleSubmit}
        />
        <FaqSection />
      </Box>

      <Button
        href={whatsappUrl}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => handleWhatsAppClick('mobile_sticky')}
        startIcon={<WhatsAppIcon />}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          left: 16,
          right: 16,
          bottom: 66,
          zIndex: 1100,
          py: 1.15,
          borderRadius: '999px',
          color: '#fff',
          backgroundColor: '#168a63',
          boxShadow: '0 12px 28px rgba(0,0,0,0.28)',
          textTransform: 'none',
          fontWeight: 900,
          '&:hover': { backgroundColor: '#107552' },
        }}
      >
        WhatsApp Fleet
      </Button>
    </>
  );
}

function HeroSection({ whatsappUrl, onWhatsAppClick }) {
  return (
    <Box
      component='section'
      aria-labelledby='fleet-hero-title'
      sx={{
        position: 'relative',
        minHeight: { xs: 680, md: 760 },
        display: 'flex',
        alignItems: 'flex-end',
        pt: { xs: 12, md: 14 },
        color: '#fff',
        backgroundColor: '#071b2b',
      }}
    >
      <Box
        component='img'
        src={heroFleet}
        alt='Solusi kendaraan listrik untuk operasional dan corporate fleet perusahaan'
        width='2560'
        height='1080'
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: {
            xs: 'linear-gradient(180deg, rgba(7,27,43,0.54) 0%, rgba(7,27,43,0.96) 74%)',
            md: 'linear-gradient(90deg, rgba(7,27,43,0.97) 0%, rgba(7,27,43,0.8) 52%, rgba(7,27,43,0.18) 100%)',
          },
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1180, mx: 'auto', px: { xs: 2, md: 5 }, pb: { xs: 6, md: 8 } }}>
        <Typography sx={heroBadge}>Corporate Mobility</Typography>
        <Typography id='fleet-hero-title' component='h1' sx={heroTitle}>
          BYD Fleet & Corporate
          <Box component='span' sx={{ display: 'block', color: '#71e1c2' }}>
            Solusi kendaraan untuk perusahaan
          </Box>
        </Typography>
        <Typography sx={heroText}>
          Konsultasi pembelian beberapa unit BYD untuk kendaraan operasional,
          company car, rental fleet, hospitality, dan transisi armada EV.
          Dilayani oleh Junius di BYD BSD Serpong untuk Jakarta, Tangerang,
          BSD, Alam Sutera, dan Jabodetabek.
        </Typography>
        <Box sx={{ mt: 3.2, display: 'flex', flexWrap: 'wrap', gap: 1.3 }}>
          <Button
            href={whatsappUrl}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => onWhatsAppClick('hero_primary')}
            variant='contained'
            sx={buttonPrimary}
          >
            Minta Penawaran Fleet
          </Button>
          <Button
            href={whatsappUrl}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => onWhatsAppClick('hero_secondary')}
            variant='outlined'
            sx={buttonGhost}
          >
            Konsultasi via WhatsApp
          </Button>
        </Box>
        <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['Jakarta', 'Tangerang', 'BSD', 'Alam Sutera', 'Jabodetabek'].map((area) => (
            <Box key={area} sx={areaChip}>{area}</Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function UseCaseSection() {
  return (
    <SectionShell eyebrow='BYD untuk kebutuhan perusahaan' title='Satu konsultasi untuk berbagai profil armada'>
      <Box sx={cardGridThree}>
        {useCases.map((item) => {
          const Icon = item.icon;
          return (
            <Box key={item.title} sx={lightCard}>
              <Box sx={iconBox}><Icon /></Box>
              <Typography component='h3' sx={cardTitle}>{item.title}</Typography>
              <Typography sx={cardText}>{item.text}</Typography>
            </Box>
          );
        })}
      </Box>
    </SectionShell>
  );
}

function BenefitSection() {
  return (
    <Box component='section' aria-labelledby='fleet-benefit-title' sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, color: '#fff', backgroundColor: '#071b2b' }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography sx={eyebrowDark}>Corporate EV Transition</Typography>
        <Typography id='fleet-benefit-title' component='h2' sx={sectionTitleDark}>
          Alasan mempertimbangkan EV untuk corporate fleet
        </Typography>
        <Box sx={{ ...cardGridTwo, mt: 3.5 }}>
          {evBenefits.map((item) => {
            const Icon = item.icon;
            return (
              <Box key={item.title} sx={darkCard}>
                <Icon sx={{ color: '#71e1c2', fontSize: 30 }} />
                <Typography component='h3' sx={{ mt: 1.4, fontSize: '1.3rem', fontWeight: 900 }}>{item.title}</Typography>
                <Typography sx={{ mt: 0.9, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}>{item.text}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function ModelSection({ onSelectModel }) {
  return (
    <SectionShell eyebrow='Pilihan kendaraan BYD' title='Bandingkan model berdasarkan kebutuhan bisnis' description='Informasi model di bawah membantu diskusi awal. Ketersediaan, harga, penawaran, dan ketentuan fleet perlu dikonfirmasi saat konsultasi.'>
      <Box sx={{ mt: 3.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
        {fleetModels.map((model) => (
          <Box key={model.name} component='article' sx={{ ...lightCard, p: 0, overflow: 'hidden' }}>
            <Box sx={{ height: 210, display: 'grid', placeItems: 'center', p: 2, backgroundColor: '#eef2f2' }}>
              <Box component='img' src={model.image} alt={`${model.name} untuk kebutuhan corporate fleet`} loading='lazy' width='560' height='315' sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </Box>
            <Box sx={{ p: 2.4 }}>
              <Typography sx={{ color: '#168a63', fontSize: '0.76rem', fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase' }}>{model.category}</Typography>
              <Typography component='h3' sx={{ mt: 0.7, fontSize: '1.4rem', fontWeight: 900 }}>{model.name}</Typography>
              <Typography sx={{ ...cardText, minHeight: 74 }}>{model.use}</Typography>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button onClick={() => onSelectModel(model.name)} variant='contained' sx={smallPrimary}>Minta Penawaran</Button>
                <Button component={RouterLink} to={model.path} variant='text' sx={textButton}>Lihat Model</Button>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </SectionShell>
  );
}

function ConsultationSection({ whatsappUrl, onWhatsAppClick }) {
  return (
    <Box component='section' sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, backgroundColor: '#dff6ee' }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '0.85fr 1.15fr' }, gap: { xs: 3, lg: 6 }, alignItems: 'center' }}>
        <Box>
          <Typography sx={eyebrow}>Corporate Fleet Consultation</Typography>
          <Typography component='h2' sx={sectionTitle}>Penawaran disusun dari kebutuhan perusahaan</Typography>
          <Typography sx={{ ...bodyText, mt: 1.5 }}>
            Pembelian fleet bukan paket yang sama untuk setiap perusahaan.
            Jumlah unit, model, registrasi, metode pembiayaan, dan lokasi
            pengiriman perlu dipetakan sebelum quotation disiapkan.
          </Typography>
          <Button href={whatsappUrl} target='_blank' rel='noopener noreferrer' onClick={() => onWhatsAppClick('consultation')} variant='contained' sx={{ ...buttonPrimary, mt: 2.5 }}>
            Diskusikan Kebutuhan Fleet
          </Button>
        </Box>
        <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.3 }}>
          {['Estimasi jumlah unit', 'Model kendaraan', 'Kebutuhan perusahaan', 'Registrasi kendaraan', 'Metode pembiayaan', 'Lokasi pengiriman'].map((item, index) => (
            <Box component='li' key={item} sx={{ p: 2, borderRadius: '10px', color: '#071b2b', backgroundColor: '#fff', border: '1px solid rgba(7,27,43,0.08)', fontWeight: 900 }}>
              <Box component='span' sx={{ mr: 1, color: '#168a63' }}>0{index + 1}</Box>{item}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ProcessSection() {
  return (
    <SectionShell eyebrow='How it works' title='Alur konsultasi pengadaan BYD fleet'>
      <Box component='ol' sx={{ m: 0, mt: 3.5, p: 0, listStyle: 'none', display: 'grid', gap: 1.3 }}>
        {processSteps.map(([number, title, text]) => (
          <Box component='li' key={number} sx={{ display: 'grid', gridTemplateColumns: { xs: '52px 1fr', md: '78px 0.45fr 1fr' }, gap: { xs: 1.5, md: 2.5 }, alignItems: 'center', p: { xs: 2, md: 2.5 }, borderRadius: '12px', backgroundColor: '#fff', border: '1px solid rgba(7,27,43,0.08)' }}>
            <Typography sx={{ color: '#168a63', fontSize: { xs: '1.2rem', md: '1.6rem' }, fontWeight: 900 }}>{number}</Typography>
            <Typography component='h3' sx={{ fontSize: '1.15rem', fontWeight: 900 }}>{title}</Typography>
            <Typography sx={{ gridColumn: { xs: '2', md: 'auto' }, color: '#52606d', lineHeight: 1.65 }}>{text}</Typography>
          </Box>
        ))}
      </Box>
    </SectionShell>
  );
}

function InquirySection({ form, errors, onChange, onFocus, onSubmit }) {
  return (
    <Box id='fleet-inquiry' component='section' aria-labelledby='fleet-form-title' sx={{ scrollMarginTop: 96, px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 }, color: '#fff', backgroundColor: '#071b2b' }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '0.72fr 1.28fr' }, gap: { xs: 4, lg: 6 }, alignItems: 'start' }}>
        <Box>
          <Typography sx={eyebrowDark}>Fleet Inquiry</Typography>
          <Typography id='fleet-form-title' component='h2' sx={sectionTitleDark}>Minta penawaran BYD untuk perusahaan</Typography>
          <Typography sx={{ mt: 1.5, color: 'rgba(255,255,255,0.72)', lineHeight: 1.8 }}>
            Isi kebutuhan dasar perusahaan. Setelah valid, detail akan dibuka
            sebagai pesan WhatsApp agar dapat Anda periksa sebelum dikirim ke
            Junius.
          </Typography>
          <Typography sx={{ mt: 2, color: 'rgba(255,255,255,0.52)', fontSize: '0.82rem', lineHeight: 1.7 }}>
            Formulir ini tidak menjanjikan harga, diskon, stok, pembiayaan,
            atau jadwal pengiriman tertentu. Informasi final mengikuti hasil
            konsultasi dan kondisi pengadaan.
          </Typography>
        </Box>

        <Box component='form' noValidate onFocusCapture={onFocus} onSubmit={onSubmit} sx={{ p: { xs: 2, md: 3 }, borderRadius: '14px', backgroundColor: '#fff', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.7 }}>
          <FleetField label='Nama' name='name' value={form.name} error={errors.name} onChange={onChange} autoComplete='name' />
          <FleetField label='Nama Perusahaan' name='company' value={form.company} error={errors.company} onChange={onChange} autoComplete='organization' />
          <FleetField label='Jabatan' name='position' value={form.position} error={errors.position} onChange={onChange} autoComplete='organization-title' />
          <FleetField label='Nomor WhatsApp' name='whatsapp' value={form.whatsapp} error={errors.whatsapp} onChange={onChange} autoComplete='tel' inputMode='tel' />
          <FleetField label='Email' name='email' type='email' value={form.email} error={errors.email} onChange={onChange} autoComplete='email' />
          <FleetField label='Kota' name='city' value={form.city} error={errors.city} onChange={onChange} autoComplete='address-level2' />
          <FleetField label='Estimasi Jumlah Unit' name='units' type='number' value={form.units} error={errors.units} onChange={onChange} inputProps={{ min: 1, max: 999 }} />
          <TextField select required label='Model yang Diminati' name='model' value={form.model} onChange={onChange} error={Boolean(errors.model)} helperText={errors.model || 'Pilih model untuk konsultasi awal.'} fullWidth>
            {fleetModels.map((model) => <MenuItem key={model.name} value={model.name}>{model.name}</MenuItem>)}
            <MenuItem value='Belum menentukan model'>Belum menentukan model</MenuItem>
          </TextField>
          <TextField label='Catatan / Kebutuhan' name='notes' value={form.notes} onChange={onChange} multiline minRows={4} fullWidth sx={{ gridColumn: '1 / -1' }} placeholder='Contoh: kendaraan operasional tim sales, lokasi pengiriman Jakarta, opsi pembiayaan perusahaan.' />
          <Button type='submit' variant='contained' sx={{ ...buttonPrimary, gridColumn: '1 / -1', justifySelf: { xs: 'stretch', sm: 'start' } }}>
            Minta Penawaran Fleet
          </Button>
          <Typography sx={{ gridColumn: '1 / -1', color: '#6b7280', fontSize: '0.78rem', lineHeight: 1.6 }}>
            Dengan melanjutkan, Anda memilih mengirim informasi tersebut
            melalui WhatsApp untuk kebutuhan konsultasi pengadaan kendaraan.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function FleetField({ error, ...props }) {
  return <TextField required fullWidth error={Boolean(error)} helperText={error || ' '} {...props} />;
}

function FaqSection() {
  return (
    <SectionShell eyebrow='FAQ BYD Corporate' title='Pertanyaan tentang pembelian BYD fleet'>
      <Box sx={{ mt: 3, display: 'grid', gap: 1.2, maxWidth: 920 }}>
        {faqItems.map((item) => (
          <Box key={item.question} component='details' sx={{ p: 2.2, borderRadius: '10px', backgroundColor: '#fff', border: '1px solid rgba(7,27,43,0.08)', '& summary': { cursor: 'pointer', color: '#071b2b', fontWeight: 900 } }}>
            <Box component='summary'>{item.question}</Box>
            <Typography sx={{ mt: 1.2, color: '#52606d', lineHeight: 1.75 }}>{item.answer}</Typography>
          </Box>
        ))}
      </Box>
    </SectionShell>
  );
}

function SectionShell({ eyebrow: label, title, description, children }) {
  return (
    <Box component='section' sx={{ px: { xs: 2, md: 6 }, py: { xs: 7, md: 9 } }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Typography sx={eyebrow}>{label}</Typography>
        <Typography component='h2' sx={sectionTitle}>{title}</Typography>
        {description && <Typography sx={{ ...bodyText, mt: 1.5, maxWidth: 840 }}>{description}</Typography>}
        {children}
      </Box>
    </Box>
  );
}

const fleetWebPageJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${absoluteUrl('/fleet')}#webpage`,
  url: absoluteUrl('/fleet'),
  name: 'BYD Fleet Indonesia | Mobil Listrik untuk Perusahaan',
  description: 'Konsultasi pengadaan kendaraan BYD untuk corporate fleet, kendaraan operasional, company car, rental, dan kebutuhan perusahaan.',
  isPartOf: { '@id': `${SITE.url}/#website` },
  about: {
    '@type': 'Service',
    name: 'Konsultasi BYD Corporate Fleet',
    serviceType: 'Konsultasi pengadaan kendaraan perusahaan',
    provider: { '@id': `${SITE.url}/#dealer` },
    areaServed: ['Jakarta', 'Tangerang', 'BSD', 'Alam Sutera', 'Jabodetabek', 'Indonesia'],
  },
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
};

const heroBadge = { display: 'inline-flex', px: 1.2, py: 0.65, borderRadius: '999px', color: '#071b2b', backgroundColor: '#71e1c2', fontSize: '0.76rem', fontWeight: 900, letterSpacing: 0.7, textTransform: 'uppercase' };
const heroTitle = { mt: 1.8, maxWidth: 900, fontSize: { xs: '2.55rem', sm: '3.7rem', md: '5.1rem' }, fontWeight: 900, lineHeight: 0.98, letterSpacing: '-0.045em' };
const heroText = { mt: 1.8, maxWidth: 760, color: 'rgba(255,255,255,0.82)', fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 };
const eyebrow = { color: '#168a63', fontSize: '0.78rem', fontWeight: 900, letterSpacing: 0.7, textTransform: 'uppercase' };
const eyebrowDark = { ...eyebrow, color: '#71e1c2' };
const sectionTitle = { mt: 1, maxWidth: 880, color: '#071b2b', fontSize: { xs: '2rem', md: '3.2rem' }, fontWeight: 900, lineHeight: 1.04, letterSpacing: '-0.03em' };
const sectionTitleDark = { ...sectionTitle, color: '#fff' };
const bodyText = { color: '#52606d', fontSize: { xs: '1rem', md: '1.05rem' }, lineHeight: 1.8 };
const lightCard = { p: 2.5, borderRadius: '12px', color: '#071b2b', backgroundColor: '#fff', border: '1px solid rgba(7,27,43,0.08)', boxShadow: '0 14px 30px rgba(7,27,43,0.06)' };
const darkCard = { p: 2.6, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.13)', backgroundColor: 'rgba(255,255,255,0.06)' };
const iconBox = { width: 48, height: 48, display: 'grid', placeItems: 'center', borderRadius: '12px', color: '#0d665e', backgroundColor: '#dff6ee' };
const cardTitle = { mt: 1.5, fontSize: '1.22rem', fontWeight: 900 };
const cardText = { mt: 0.8, color: '#52606d', lineHeight: 1.7 };
const cardGridThree = { mt: 3.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2 };
const cardGridTwo = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 };
const areaChip = { px: 1.4, py: 0.75, borderRadius: '999px', border: '1px solid rgba(255,255,255,0.18)', backgroundColor: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.82)', fontSize: '0.82rem', fontWeight: 800 };
const buttonPrimary = { px: 3.2, py: 1.15, borderRadius: '999px', color: '#071b2b', backgroundColor: '#71e1c2', textTransform: 'none', fontWeight: 900, '&:hover': { backgroundColor: '#55ceb0' } };
const buttonGhost = { px: 3.2, py: 1.15, borderRadius: '999px', color: '#fff', borderColor: 'rgba(255,255,255,0.65)', textTransform: 'none', fontWeight: 900, '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' } };
const smallPrimary = { borderRadius: '999px', color: '#fff', backgroundColor: '#0d665e', textTransform: 'none', fontWeight: 900, '&:hover': { backgroundColor: '#09544d' } };
const textButton = { borderRadius: '999px', color: '#0d665e', textTransform: 'none', fontWeight: 900 };

export default Fleet;
